import React, { useState, useEffect } from 'react';
import { Box, Paper, Stack, Typography, Chip, Tooltip as MuiTooltip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Tooltip,
} from 'recharts';

/**
 * Shared visual kit for the analytics/dashboard pages.
 * One consistent modern language: soft-rounded cards, tinted icon bubbles,
 * gradient area charts, donut breakdowns, compact number formatting.
 */

export const CHART_COLORS = [
  '#1565c0', '#00897b', '#7b1fa2', '#ef6c00', '#c62828',
  '#2e7d32', '#5c6bc0', '#00acc1', '#f9a825', '#8d6e63',
];

// ─── Formatters ────────────────────────────────────────────────────────────────

export const fmtUsd = (n) => (n === null || n === undefined ? '—' : `$${Number(n).toFixed(2)}`);
export const fmtNum = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
export const fmtCompact = (n) => {
  if (n === null || n === undefined) { return '—'; }
  const v = Number(n);
  if (Math.abs(v) >= 1e9) { return `${(v / 1e9).toFixed(1)}B`; }
  if (Math.abs(v) >= 1e6) { return `${(v / 1e6).toFixed(1)}M`; }
  if (Math.abs(v) >= 1e3) { return `${(v / 1e3).toFixed(1)}k`; }
  return v.toLocaleString();
};
export const fmtBytes = (n) => {
  if (n === null || n === undefined) { return '—'; }
  const v = Number(n);
  if (v >= 1024 ** 3) { return `${(v / 1024 ** 3).toFixed(2)} GB`; }
  if (v >= 1024 ** 2) { return `${(v / 1024 ** 2).toFixed(1)} MB`; }
  if (v >= 1024) { return `${(v / 1024).toFixed(1)} KB`; }
  return `${Math.round(v)} B`;
};
/** Lambda compute: MB-ms → human GB-seconds. */
export const fmtGbS = (mbMs) => {
  if (!mbMs) { return '0 GB-s'; }
  const gbs = mbMs / 1024 / 1000;
  return gbs >= 1000 ? `${fmtCompact(Math.round(gbs))} GB-s` : `${gbs.toFixed(2)} GB-s`;
};

// ─── Cards ─────────────────────────────────────────────────────────────────────

const CARD_SX = {
  p: 2.5,
  borderRadius: 3,
  height: '100%',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 3px rgba(15,23,42,.06)',
  transition: 'box-shadow .2s',
  '&:hover': { boxShadow: '0 4px 14px rgba(15,23,42,.10)' },
};

/**
 * Headline stat with tinted icon bubble, optional trend chip and sparkline.
 * @param trend  number (% change) — renders ▲/▼ chip; omit to hide
 * @param spark  [{ v }] tiny series rendered behind the value
 */
export function StatCard({ label, value, sub, icon, color = '#1565c0', trend, trendLabel, spark, sparkKey = 'v' }) {
  const up = typeof trend === 'number' && trend >= 0;
  const sparkId = `spark-${String(label).replace(/\W+/g, '')}`; // SVG ids can't contain spaces
  return (
    <Paper elevation={0} sx={{ ...CARD_SX, position: 'relative', overflow: 'hidden' }}>
      {spark && spark.length > 1 && (
        <Box sx={{ position: 'absolute', inset: 0, top: 'auto', height: 56, opacity: 0.55, pointerEvents: 'none' }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={sparkId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey={sparkKey} stroke={color} strokeWidth={1.5}
                fill={`url(#${sparkId})`} dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ position: 'relative' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
          <Typography variant="h4" fontWeight={800} sx={{ my: 0.25, letterSpacing: '-0.5px' }}>{value}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {typeof trend === 'number' && (
              <Chip
                size="small"
                icon={up ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                label={`${up ? '+' : ''}${trend.toFixed(0)}%${trendLabel ? ` ${trendLabel}` : ''}`}
                sx={{
                  height: 20, fontSize: 11, fontWeight: 700,
                  bgcolor: up ? 'rgba(46,125,50,.12)' : 'rgba(198,40,40,.12)',
                  color: up ? 'success.dark' : 'error.dark',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            )}
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Stack>
        </Box>
        {icon && (
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, display: 'flex', alignItems: 'center',
            justifyContent: 'center', color, bgcolor: `${color}1a`, flexShrink: 0,
          }}>
            {icon}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

/** Card wrapper for charts/tables with a consistent header. */
export function ChartCard({ title, subtitle, action, children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...CARD_SX, p: 0, display: 'flex', flexDirection: 'column', ...sx }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
        sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {action}
      </Stack>
      <Box sx={{ px: 1.5, pb: 1.5, flex: 1, minHeight: 0 }}>{children}</Box>
    </Paper>
  );
}

/** Gradient defs helper for AreaCharts. */
export function areaGradient(id, color) {
  return (
    <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={color} stopOpacity={0.28} />
      <stop offset="100%" stopColor={color} stopOpacity={0.02} />
    </linearGradient>
  );
}

/** Donut with center label. data: [{ name, value }] */
export function Donut({ data, height = 220, centerLabel, centerValue, valueFormatter = fmtUsd, colors = CHART_COLORS }) {
  const items = (data || []).filter((d) => d.value > 0);
  return (
    <Box sx={{ position: 'relative', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={items} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="88%"
            paddingAngle={2} strokeWidth={0}>
            {items.map((d, i) => <Cell key={d.name} fill={colors[i % colors.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => valueFormatter(v)} />
        </PieChart>
      </ResponsiveContainer>
      <Box sx={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
      }}>
        <Typography variant="h6" fontWeight={800}>{centerValue}</Typography>
        {centerLabel && <Typography variant="caption" color="text.secondary">{centerLabel}</Typography>}
      </Box>
    </Box>
  );
}

/** Compact legend for a Donut. */
export function DonutLegend({ data, valueFormatter = fmtUsd, colors = CHART_COLORS, max = 6 }) {
  const items = (data || []).filter((d) => d.value > 0).slice(0, max);
  return (
    <Stack spacing={0.75} sx={{ px: 1 }}>
      {items.map((d, i) => (
        <Stack key={d.name} direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: colors[i % colors.length], flexShrink: 0 }} />
          <MuiTooltip title={d.name}><Typography variant="caption" noWrap sx={{ flex: 1 }}>{d.name}</Typography></MuiTooltip>
          <Typography variant="caption" fontWeight={700}>{valueFormatter(d.value)}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/** % change between the second and first half of a numeric series. */
export function trendFromSeries(series, key) {
  if (!series || series.length < 4) { return undefined; }
  const mid = Math.floor(series.length / 2);
  const sum = (arr) => arr.reduce((a, r) => a + (Number(r[key]) || 0), 0);
  const prev = sum(series.slice(0, mid));
  const cur = sum(series.slice(mid));
  if (prev === 0) { return undefined; }
  return ((cur - prev) / prev) * 100;
}

/**
 * Live auto-refresh indicator for pages that poll on an interval. Shows the
 * refresh rate and a per-second countdown to the next refresh. Pass the poll
 * interval and the timestamp (ms) of the last successful fetch so the countdown
 * stays in sync with the actual data refresh.
 */
export function RefreshIndicator({ intervalMs, lastRefreshAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!intervalMs) { return null; }
  const secs = Math.round(intervalMs / 1000);
  const base = lastRefreshAt || now;
  const remaining = Math.max(0, Math.ceil((base + intervalMs - now) / 1000));
  return (
    <MuiTooltip title={`This page refreshes automatically every ${secs}s.`}>
      <Chip size="small" variant="outlined" color="primary" icon={<AutorenewIcon fontSize="small" />}
        label={`Auto-refresh ${secs}s · next in ${remaining}s`}
        sx={{ fontWeight: 600, '& .MuiChip-icon': { fontSize: 16 } }} />
    </MuiTooltip>
  );
}

/**
 * Notice for pages that load once and do NOT auto-refresh — an info chip telling
 * the user to reload manually to see fresh data.
 */
export function ManualRefreshNotice({ label = 'Manual refresh' }) {
  return (
    <MuiTooltip title="This page does not refresh on its own. Reload the page to update the data.">
      <Chip size="small" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}
        label={label}
        sx={{ fontWeight: 600, color: 'text.secondary', '& .MuiChip-icon': { fontSize: 16 } }} />
    </MuiTooltip>
  );
}

// Named exports only — an anonymous default export trips
// import/no-anonymous-default-export (a build failure under CI=true).
